import React, { useContext, useMemo, useState } from 'react';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import { Phone as PhoneIcon, Schedule as ScheduleIcon } from '@material-ui/icons';
import { PopulatedTimeslot, EntityResponse, Premise, CampaignService } from "../../types";
import { makeStyles, createStyles, Theme, useTheme, useMediaQuery } from '@material-ui/core';
import { LocalizationContext } from '../LanguageContext/LocalizationContext';
import { getPrettyDate, getLengthOfAppointment, parsePrice, getTime, isRehabilitation } from "../utils";
import moment from 'moment';
import { ExpertPictureNameAndTitle } from '../ExpertProfile/ExpertPictureNameAndTitle';
import { DefaultButton, SuccessButton } from '../CustomStyleComponents/Buttons';
import strongAuthService from '../../services/strongAuthService';
import memoryStorage from '../../util/memoryStorage';

const useStyles = (mobile: boolean) => makeStyles((theme: Theme) =>
    createStyles({
        map: {
            display: "block",
            position: "relative",
            height: 172,
            minHeight: 172,
            width: "100%",
            marginBottom: theme.spacing(2)
        },
        paper: {
            justifyContent: "center",
            height: "100%",
            "& >.MuiGrid-item:not(:last-child)": {
                paddingBottom: theme.spacing(2),
            },
            "& >.MuiGrid-item:first-child": {
                paddingRight: mobile ? 0 : theme.spacing(2)
            }
        },
        blue: {
            color: theme.palette.text.secondary
        },
        serviceName: {
            color: theme.palette.text.secondary,
            paddingTop: theme.spacing(2),
        },
        serviceDescription: {
            paddingBottom: theme.spacing(2)
        },
        mobileButtons: {
            paddingBottom: theme.spacing(2),
            "&:last-child": {
                paddingBottom: 0
            }
        },
        desktopButtons: {
            "& >.MuiGrid-item:first-child": {
                paddingRight: theme.spacing(4)
            }
        },
        buttonContainer: {
            display: "flex",
            flex: 1,
            flexDirection: "column",
        },
        loading: {
            width: "4em",
            height: "100%",
            margin: "auto",
        },
        phoneDialog: {
            '& .MuiDialog-paper': {
                borderRadius: theme.spacing(2),
                minWidth: mobile ? '90%' : 400,
                maxWidth: mobile ? '95%' : 500,
            }
        },
        phoneDialogTitle: {
            textAlign: 'center',
            paddingBottom: theme.spacing(1),
            '& .MuiTypography-h6': {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: theme.spacing(1),
                color: theme.palette.primary.main,
                fontWeight: 600,
            }
        },
        phoneDialogContent: {
            textAlign: 'center',
            paddingTop: 0,
            paddingBottom: theme.spacing(2),
        },
        phoneNumber: {
            color: theme.palette.primary.main,
            fontWeight: 'bold',
            fontSize: '1.2rem',
            margin: theme.spacing(2, 0),
            padding: theme.spacing(1),
            backgroundColor: theme.palette.grey[50],
            borderRadius: theme.spacing(1),
            border: `1px solid ${theme.palette.grey[200]}`,
        },
        serviceHours: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: theme.spacing(1),
            color: theme.palette.text.secondary,
            marginTop: theme.spacing(1),
        },
        phoneDialogActions: {
            justifyContent: 'center',
            paddingBottom: theme.spacing(2),
        }
    }));

interface ReservationMethodSelectionProps {
    timeslot: PopulatedTimeslot,
    serviceName: string,
    premise: EntityResponse<Premise> | null,
    service: CampaignService | null,
    handleNext: () => void,
    handleClose: () => void,
    handleBack: () => void
}

export const ReservationMethodSelection: React.FC<ReservationMethodSelectionProps> = ({
    timeslot,
    serviceName,
    premise,
    service,
    handleClose,
    handleNext,
    handleBack,
}) => {
    const theme = useTheme();
    const windowIsMdSize = useMediaQuery(theme.breakpoints.up('md'));
    const classes = useStyles(!windowIsMdSize)();
    const { translation } = useContext(LocalizationContext);
    const [phoneDialogOpen, setPhoneDialogOpen] = useState(false);

    const yritys = premise && premise.data.Nimi.yritys ?
        premise.data.Nimi.yritys + " " : "";

    const handleWebReservation = (bookForOther: boolean) => () => {
        if (strongAuthService.authenticated()) {
            if (strongAuthService.minutesUntilTokenExpires() < 9) {
                // User should have enough time to comfortably type in their information.
                strongAuthService.authenticate(timeslot.uuid, bookForOther);
            } else {
                memoryStorage.setItem('reservationUuid', timeslot.uuid);
                memoryStorage.setItem('bookForOther', bookForOther);

                handleNext();
            }
        }
        else strongAuthService.authenticate(timeslot.uuid, bookForOther);
    };

    const handlePhoneReservation = () => {
        setPhoneDialogOpen(true);
    };

    const handleClosePhoneDialog = () => {
        setPhoneDialogOpen(false);
    };

    const bookForMyselfButton = useMemo(() =>
        <SuccessButton
            color="primary"
            onClick={handleWebReservation(false)}
            fullWidth
            id="bookForMyselfButton"
        >
            {translation.bookTimeForMyself}
        </SuccessButton>
    , [translation, handleWebReservation]);

    const bookForSomeoneElseButton = useMemo(() =>
        <SuccessButton
            color="primary"
            onClick={handleWebReservation(true)}
            fullWidth
            id="bookForSomeoneElseButton"
        >
            {translation.bookTimeForSomeoneElse}
        </SuccessButton>
    , [translation, handleWebReservation]);

    const bookByPhoneButton = useMemo(() =>
        <SuccessButton
            color="primary"
            onClick={handlePhoneReservation}
            fullWidth
            id="bookByPhoneButton"
        >
            {translation.bookTimeByPhone}
        </SuccessButton>
    , [translation]);

    const abortButton = useMemo(() =>
        <DefaultButton
            onClick={handleClose}
            fullWidth
            id={"closeModal"}
        >
            {translation.buttonAbort}
        </DefaultButton>
    , [translation, handleClose]);

    const backButton = useMemo(() =>
        <DefaultButton
            onClick={handleBack}
            fullWidth
        >
            {translation.buttonGoBack}
        </DefaultButton>
    , []);

    const bottomButtonsMobile = useMemo(() =>
        <Grid item className={classes.buttonContainer} style={{ marginTop: '-10px' }}>
            <div style={{ flex: 1 }} />
            <div className={classes.mobileButtons}>
                {
                    windowIsMdSize ?
                        backButton :
                        abortButton
                }
            </div>
            <div className={classes.mobileButtons}>
                {
                    windowIsMdSize ?
                        abortButton :
                        backButton
                }
            </div>
        </Grid>
    , [classes, windowIsMdSize]);

    const bottomButtonsDesktop = useMemo(() =>
        <Grid item container xs={12} className={classes.desktopButtons} style={{ marginTop: '-15px' }}>
            <Grid item md={6} xs={12}>
                {
                    backButton
                }
            </Grid>
            <Grid item md={6} xs={12}>
                {
                    abortButton
                }
            </Grid>
        </Grid>
    , [classes]);

    return (
        <>
            <Grid
                container
                item xs={12}
                sm={6}
            >
                <Grid item xs={12}>
                    <Typography
                        variant={"subtitle1"}
                        className={classes.blue}
                    >
                        {
                            `${getPrettyDate(moment.utc(timeslot.start).local(), translation)}` +
                            ` ${translation.clock} ${getTime(timeslot.start)}`
                        }
                    </Typography>
                    <Typography
                        variant={"subtitle1"}
                        className={classes.serviceName}
                    >
                        {`${serviceName} ${getLengthOfAppointment(timeslot)} min`}
                    </Typography>
                    {service &&
                        <Typography
                            className={classes.serviceDescription}
                            variant="subtitle2"
                        >
                            {service.Kuvaus}
                        </Typography>
                    }
                    <ExpertPictureNameAndTitle
                        expert={timeslot.työntekijä}
                        size={"small"}
                    />
                </Grid>

                {timeslot.hinta &&
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" className={classes.blue}>
                            {translation.price} {parsePrice(timeslot.hinta)}€
                        </Typography>
                    </Grid>
                }

                {isRehabilitation(yritys)>=0 ?"Palvelun hintaan lisätään toimisto- ja Kantamaksu 5 euroa / käynti":""}

            </Grid>
            <Grid
                item xs={12}
                md={6}
            >
                <Typography variant="body1" style={{marginBottom: '10px'}}>
                    {translation.bookingRequiresIdentification}
                </Typography>
                <div style={{ marginBottom: '15px' }}>
                    { bookForMyselfButton }
                </div>
                <div style={{ marginBottom: '30px' }}>
                    { bookForSomeoneElseButton }
                </div>
                <Typography variant="body1"  style={{ marginBottom: '10px' }}>
                    {translation.bookingIsAvailableByPhone}
                </Typography>
                <div style={{ marginBottom: '30px' }}>
                    { bookByPhoneButton }
                </div>
            </Grid>
            {windowIsMdSize ? bottomButtonsDesktop : bottomButtonsMobile}

            {/* Phone Reservation Dialog */}
            <Dialog
                open={phoneDialogOpen}
                onClose={handleClosePhoneDialog}
                className={classes.phoneDialog}
                aria-labelledby="phone-reservation-dialog-title"
            >
                <DialogTitle id="phone-reservation-dialog-title" className={classes.phoneDialogTitle}>
                    <Typography variant="h6">
                        <PhoneIcon />
                        {translation.phoneReservationDialogTitle}
                    </Typography>
                </DialogTitle>
                <DialogContent className={classes.phoneDialogContent}>
                    <Typography variant="body1" gutterBottom>
                        {translation.phoneReservationCompanyName}
                    </Typography>
                    <Typography variant="body1" className={classes.phoneNumber}>
                        {translation.phoneReservationPhoneNumber}
                    </Typography>
                    <div className={classes.serviceHours}>
                        <ScheduleIcon fontSize="small" />
                        <Typography variant="body2">
                            {translation.phoneReservationServiceHours}
                        </Typography>
                    </div>
                </DialogContent>
                <DialogActions className={classes.phoneDialogActions}>
                    <Button
                        onClick={handleClosePhoneDialog}
                        color="primary"
                        variant="contained"
                    >
                        {translation.buttonClose}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

interface ReservationMethodSelectionViewProps extends ReservationMethodSelectionProps {
    handleNext: () => void,
    handleClose: () => void,
    handleBack: () => void
}

export const ReservationMethodSelectionView: React.FC<ReservationMethodSelectionViewProps> = ({
    timeslot,
    serviceName,
    premise,
    service,
    handleClose,
    handleNext,
    handleBack,
}) => {
    const theme = useTheme();
    const windowIsMdSize = useMediaQuery(theme.breakpoints.up('md'));

    const classes = useStyles(!windowIsMdSize)();

    return (
        <Grid
            item
            container
            xs={12}
            className={classes.paper}
        >
            <ReservationMethodSelection
                timeslot={timeslot}
                serviceName={serviceName}
                premise={premise}
                service={service}
                handleClose={handleClose}
                handleNext={handleNext}
                handleBack={handleBack}
            />
        </Grid>
    );
};

export default ReservationMethodSelectionView;
